import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { useMaterialUIController } from "context";
import { formatLocalDateTime } from "utils/dateFormatter";
import { request } from "app/network";

import GenericListScreen from "components/GenericListScreen/GenericListScreen";
import { Column } from "components/GenericListScreen/types";
import InquiryDetailModal from "./InquiryDetailModal";

// ── 상수 ──────────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
    INQUIRY_ACCOUNT_DELETE: "계정 삭제 요청",
    INQUIRY_POINT:          "포인트 문의",
    INQUIRY_APP:            "앱 오류/버그",
    INQUIRY_ETC:            "기타",
};

const STATUS_COLOR_MAP: Record<string, "warning" | "info" | "success" | "default"> = {
    INQUIRY_PENDING:     "warning",
    INQUIRY_IN_PROGRESS: "info",
    INQUIRY_DONE:        "success",
    INQUIRY_CLOSED:      "default",
};

// ── 컴포넌트 ──────────────────────────────────────────────
export default function InquiryListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient();

    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    // 공통코드 INQUIRY_STATUS 목록 조회
    const [statusOptions, setStatusOptions] = useState<{ code: string; name: string }[]>([]);
    useEffect(() => {
        request("CommonCode.getByGroup", {
            pathParams: { groupCode: "INQUIRY_STATUS" }
        }).then(res => {
            setStatusOptions(res.data ?? []);
        }).catch(e => console.error("INQUIRY_STATUS 공통코드 로드 실패:", e));
    }, []);

    // code → name 빠른 조회용 Map
    const statusNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        statusOptions.forEach(o => { map[o.code] = o.name; });
        return map;
    }, [statusOptions]);

    const handleRowClick = (row: any) => {
        setSelectedItem(row);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedItem(null);
    };

    // ── 컬럼 정의 ─────────────────────────────────────────
    const columns: Column<any>[] = useMemo(() => [
        {
            Header: "번호",
            accessor: "seqId",
            width: "5%",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {value}
                </MDTypography>
            ),
        },
        {
            Header: "유형",
            accessor: "categoryCode",
            width: "12%",
            Cell: ({ row }: any) => (
                <MDTypography variant="caption" color="text">
                    {row.original.categoryName ?? CATEGORY_LABELS[row.original.categoryCode] ?? row.original.categoryCode}
                </MDTypography>
            ),
        },
        {
            Header: "제목",
            accessor: "title",
            width: "28%",
            Cell: ({ row }: any) => (
                <Tooltip title={row.original.title ?? ""} placement="top-start">
                    <MDTypography
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 300,
                            cursor: "pointer",
                            textDecoration: "underline",
                            textDecorationColor: "rgba(0,150,255,0.3)",
                        }}
                    >
                        {row.original.title}
                    </MDTypography>
                </Tooltip>
            ),
        },
        {
            Header: "문의자",
            accessor: "name",
            width: "10%",
            Cell: ({ row }: any) => (
                <MDTypography variant="caption" color="text">
                    {row.original.name || "-"}
                </MDTypography>
            ),
        },
        {
            Header: "연락처",
            accessor: "contact",
            width: "15%",
            Cell: ({ row }: any) => (
                <MDBox>
                    {row.original.phone && (
                        <MDTypography variant="caption" color="text" display="block">
                            📞 {row.original.phone}
                        </MDTypography>
                    )}
                    {row.original.email && (
                        <MDTypography variant="caption" color="text" display="block">
                            ✉ {row.original.email}
                        </MDTypography>
                    )}
                    {!row.original.phone && !row.original.email && (
                        <MDTypography variant="caption" color="text">-</MDTypography>
                    )}
                </MDBox>
            ),
        },
        {
            Header: "상태",
            accessor: "statusCode",
            width: "10%",
            Cell: ({ row }: any) => {
                const code = row.original.statusCode;
                const label = statusNameMap[code] ?? row.original.statusName ?? code;
                const chipColor = STATUS_COLOR_MAP[code] ?? "default";
                return (
                    <Chip
                        label={label}
                        color={chipColor}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            ...((chipColor === "warning" || chipColor === "info" || chipColor === "success") && {
                                color: "#fff",
                                "& .MuiChip-label": { color: "#fff" },
                            }),
                        }}
                    />
                );
            },
        },
        {
            Header: "등록일",
            accessor: "crtDt",
            width: "12%",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color="text">
                    {value ? formatLocalDateTime(value) : "-"}
                </MDTypography>
            ),
        },
        {
            Header: "처리일",
            accessor: "answeredAt",
            width: "12%",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color="text">
                    {value ? formatLocalDateTime(value) : "-"}
                </MDTypography>
            ),
        },
    ], [statusNameMap]);

    // ── 데이터 변환 ───────────────────────────────────────
    const dataTransform = (data: any) => ({
        items: data.items ?? [],
        totalCount: data.totalCount ?? 0,
    });

    return (
        <>
            <GenericListScreen
                title="1:1 문의 목록"
                icon="support_agent"
                apiName="Inquiry.pagedList"
                columns={columns}
                dataTransform={dataTransform}
                onRowClick={handleRowClick}
                actionComponent={
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                            <Icon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }}>info_outline</Icon>
                            행을 클릭하면 상세 내용을 확인할 수 있습니다
                        </MDTypography>
                    </MDBox>
                }
            />

            <InquiryDetailModal
                open={modalOpen}
                item={selectedItem}
                onClose={handleModalClose}
            />
        </>
    );
}
